// ============================================================================
//  SENTRY ERROR MONITORING + APP ENTRY POINT

// ============================================================================
import * as Sentry from "@sentry/react";
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ChakraProvider } from '@chakra-ui/react';
import theme from './theme/designSystem';
import App from './App';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';

// ============================================================================
// 🔇 PRODUCTION CONSOLE SILENCE
// ============================================================================
// The app carries ~1,100 debug statements across 160+ files - useful while
// building, but in production they stream into anyone's devtools on every
// hand ("[RUNOUT] hasAllIn", "[EQUITY] Board cards", table and user ids and
// so on). That is noise, a small information leak, and the loudest possible
// tell that a build is unpolished.
//
// log/info/debug are silenced in production builds only. error and warn are
// deliberately KEPT so genuine failures still surface in Sentry, in bug
// reports, and for support. Development is untouched.
// ============================================================================
if (process.env.NODE_ENV === 'production') {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.debug = noop;
}

// ============================================================================
//  SENTRY INITIALIZATION

// ============================================================================
Sentry.init({
  dsn: "https://f6323762589e183056a6723b08f3f1f3@o4510644198703104.ingest.us.sentry.io/4510644199620608",
  integrations: [
    Sentry.browserTracingIntegration(),
    // Replay can cause 403 errors on some setups - disable if issues persist
    Sentry.replayIntegration(),
  ],
  // Performance monitoring - reduce in production
  tracesSampleRate: 0.5,
  // Session replay settings
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Environment
  environment: process.env.NODE_ENV || 'production',
  // Only trace requests to your own API
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/pokersweepstars\.web\.app/,
    /^https:\/\/us-central1-pokersweepstars\.cloudfunctions\.net/,
  ],
  // Ignore common non-errors
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured',
  ],
});

// ============================================================================
//  CAPACITOR INITIALIZATION FOR MOBILE APP

// ============================================================================
const initMobileApp = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Configure status bar for dark theme
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#0D1B2A' });
      
      // Hide splash screen after app is ready
      await SplashScreen.hide();
      
      // Configure keyboard behavior
      if (Capacitor.getPlatform() === 'ios') {
        // iOS specific keyboard handling
        Keyboard.setAccessoryBarVisible({ isVisible: true });
      }
      
      console.log('📱 Mobile app initialized successfully');
    } catch (error) {
      console.warn('Mobile init warning:', error);
    }
  }
};

// ============================================================================
//  SAFE AREA CSS VARIABLES

// ============================================================================
// Inject safe area CSS variables for notches, home indicators, etc.
const injectSafeAreaCSS = () => {
  const style = document.createElement('style');
  style.textContent = `
    /* Safe Area Support for iOS notches and Android cutouts */
    :root {
      --safe-area-inset-top: env(safe-area-inset-top, 0px);
      --safe-area-inset-right: env(safe-area-inset-right, 0px);
      --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
      --safe-area-inset-left: env(safe-area-inset-left, 0px);
    }
    
    /* NOTE: no html-level safe-area padding here. The app shell Box in
       App.tsx applies the top inset exactly once - a global html padding
       STACKED with it and doubled the empty band above every page. */
    
    /* Prevent overscroll/bounce on iOS */
    body {
      overscroll-behavior: none;
      -webkit-overflow-scrolling: touch;
    }
    
    /* Fix for iOS input zoom */
    input, select, textarea {
      font-size: 16px !important;
    }
    
    /* Disable text selection on game elements */
    .no-select {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
    
    /* Disable tap highlight on mobile */
    * {
      -webkit-tap-highlight-color: transparent;
    }
    
    /* Safe area padding utilities */
    .safe-area-top {
      padding-top: env(safe-area-inset-top, 0px);
    }
    .safe-area-bottom {
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }
    .safe-area-left {
      padding-left: env(safe-area-inset-left, 0px);
    }
    .safe-area-right {
      padding-right: env(safe-area-inset-right, 0px);
    }
    .safe-area-all {
      padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
    }
  `;
  document.head.appendChild(style);
};

// Inject safe area CSS immediately
injectSafeAreaCSS();

// Initialize mobile features
initMobileApp();

// ============================================================================
// REACT APP RENDER
// ============================================================================
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <ChakraProvider theme={theme}>
      <App />
    </ChakraProvider>
  </React.StrictMode>
);